import React from 'react'
import { Button, ButtonGroup, Dropdown, Form } from 'react-bootstrap'
import { Search, Filter as FilterIcon, ArrowUpDown, Grid, List } from 'lucide-react'

export type FilterType = 'all' | 'parent' | 'branch' | 'leaf'

interface SortItem {
  key: string
  label: string
}

interface FilterBarProps {
  search: string
  onSearchChange: (value: string) => void
  filterType: FilterType
  onFilterTypeChange: (value: FilterType) => void
  sortBy: string
  onSortChange: (key: string) => void
  sortOptions: SortItem[]
  viewMode: 'grid' | 'list'
  onViewModeChange: (mode: 'grid' | 'list') => void
}

const FilterBar: React.FC<FilterBarProps> = ({
  search,
  onSearchChange,
  filterType,
  onFilterTypeChange,
  sortBy,
  onSortChange,
  sortOptions,
  viewMode,
  onViewModeChange,
}) => {
  const activeSortLabel = sortOptions.find((o) => o.key === sortBy)?.label || 'Sort by'

  return (
    <div className="bg-dark border-bottom border-secondary d-flex align-items-center justify-content-between flex-wrap gap-3 px-4 py-3">
      <div className="d-flex align-items-center gap-3">
        <div className="position-relative" style={{ minWidth: 320 }}>
          <Search
            size={18}
            className="position-absolute"
            style={{ left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#b0b0b0' }}
          />
          <Form.Control
            type="text"
            placeholder="Search models by name..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="bg-dark text-white"
            style={{
              paddingLeft: '2.75rem',
              height: 46,
              border: '2px solid #404040',
              borderRadius: 12,
            }}
          />
        </div>

        <div className="d-flex align-items-center gap-2">
          {(
            [
              { key: 'all', label: 'All Types' },
              { key: 'parent', label: 'Foundation' },
              { key: 'branch', label: 'Branch' },
              { key: 'leaf', label: 'Specialized' },
            ] as const
          ).map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => onFilterTypeChange(opt.key as any)}
              className={`d-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-pill ${
                filterType === opt.key
                  ? 'bg-primary text-white border border-primary'
                  : 'bg-dark text-secondary border'
              }`}
              style={{ borderWidth: 2 }}
            >
              {opt.key === 'all' && <FilterIcon size={14} />}
              {opt.label}
            </button>
          ))}
        </div>

        <Dropdown>
          <Dropdown.Toggle variant="outline-secondary" size="sm">
            <ArrowUpDown size={14} className="me-1" />
            {activeSortLabel}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {sortOptions.map((opt) => (
              <Dropdown.Item key={opt.key} onClick={() => onSortChange(opt.key)}>
                {opt.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="d-flex align-items-center gap-2">
        <ButtonGroup>
          <Button
            variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
            onClick={() => onViewModeChange('grid')}
          >
            <Grid size={16} />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
            onClick={() => onViewModeChange('list')}
          >
            <List size={16} />
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}

export default FilterBar
